import { editorStore, useEditorSelector } from './editorStore';

interface FieldProps {
  blockId: string;
  props: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
  field: InspectorFieldLocal;
  path?: string[];
}

interface InspectorFieldLocal {
  kind: string;
  key: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  options?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number;
  altKey?: string;
  itemFields?: InspectorFieldLocal[];
}

function getPathValue(base: unknown, path: string[]): unknown {
  return path.reduce<unknown>((current, key) => {
    if (typeof current !== 'object' || current === null) {
      return undefined;
    }
    return (current as Record<string, unknown>)[key];
  }, base);
}

function setPathValue(base: Record<string, unknown>, path: string[], value: unknown): Record<string, unknown> {
  const root = { ...base };
  let cursor: Record<string, unknown> = root;

  for (const key of path.slice(0, -1)) {
    const next = cursor[key];
    if (!next || typeof next !== 'object' || Array.isArray(next)) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }

  const finalKey = path[path.length - 1];
  if (finalKey === undefined) {
    return root;
  }

  if (value === undefined) {
    delete cursor[finalKey];
  } else {
    cursor[finalKey] = value;
  }

  return root;
}

function fieldPath(field: InspectorFieldLocal): string[] {
  return field.key.split('.').filter(Boolean);
}

export default function InspectorFields({
  blockId,
  props,
  field,
  onChange
}: FieldProps) {
  const path = fieldPath(field);
  const currentValue = getPathValue(props, path);

  const update = (value: unknown): void => {
    const patch = setPathValue({}, path, value);
    const topKey = path[0];
    if (topKey === undefined) {
      onChange(patch);
      return;
    }
    onChange({ [topKey]: patch[topKey] });
  };

  // Hooks must run on every render in the same order, so this is read before the switch, not inside a case.
  const pendingImage = useEditorSelector((state) => state.pendingImageTarget);

  switch (field.kind) {
    case 'text':
      return (
        <label className="inspector-row">
          <span className="inspector-label">{field.label}</span>
          <input
            type="text"
            value={String(currentValue ?? '')}
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            onChange={(event) => update(event.target.value)}
          />
        </label>
      );

    case 'richtext':
      return (
        <div className="inspector-row">
          <span className="inspector-label">{field.label}</span>
          <textarea
            rows={4}
            value={String(currentValue ?? '')}
            onChange={(event) => update(event.target.value)}
          />
        </div>
      );

    case 'link': {
      const linkValue = (currentValue ?? {}) as { label?: string; href?: string };
      return (
        <div className="inspector-row inspector-row--stacked">
          <span className="inspector-label">{field.label}</span>
          <input
            type="text"
            value={linkValue.label ?? ''}
            placeholder="Label"
            onChange={(event) => update({ ...(currentValue as Record<string, unknown>), label: event.target.value })}
          />
          <input
            type="text"
            value={linkValue.href ?? ''}
            placeholder="https://"
            onChange={(event) => update({ ...(currentValue as Record<string, unknown>), href: event.target.value })}
          />
        </div>
      );
    }

    case 'image': {
      const imageValue = (currentValue ?? {}) as { assetId?: string | null; alt?: string };
      const altKey = field.altKey ?? '';
      const assigned = pendingImage?.blockId === blockId && pendingImage.propKey === field.key;

      return (
        <div className="inspector-row inspector-row--stacked">
          <span className="inspector-label">{field.label}</span>
          <button
            type="button"
            className="inspector-image-button"
            onClick={() => editorStore.startImagePick(blockId, field.key, altKey)}
          >
            {assigned ? 'Choose another image…' : 'Choose image…'}
          </button>
          {imageValue.assetId ? (
            <span className="inspector-hint">Image assigned · {imageValue.assetId}</span>
          ) : (
            <span className="inspector-hint">No image selected</span>
          )}
          <input
            type="text"
            value={imageValue.alt ?? ''}
            placeholder="Alt text"
            onChange={(event) => update({ ...(currentValue as Record<string, unknown>), alt: event.target.value })}
          />
        </div>
      );
    }

    case 'select':
      return (
        <label className="inspector-row">
          <span className="inspector-label">{field.label}</span>
          <select
            value={String(currentValue ?? '')}
            onChange={(event) => update(event.target.value)}
          >
            {(field.options ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      );

    case 'color':
      return (
        <label className="inspector-row">
          <span className="inspector-label">{field.label}</span>
          <input
            type="color"
            value={String(currentValue ?? '#ffffff')}
            onChange={(event) => update(event.target.value)}
          />
        </label>
      );

    case 'number':
      return (
        <label className="inspector-row">
          <span className="inspector-label">{field.label}</span>
          <input
            type="number"
            value={typeof currentValue === 'number' ? currentValue : Number(currentValue ?? 0)}
            min={field.min}
            max={field.max}
            step={field.step}
            onChange={(event) => update(Number(event.target.value))}
          />
        </label>
      );

    case 'boolean':
      return (
        <label className="inspector-row inspector-row--between">
          <span className="inspector-label">{field.label}</span>
          <input
            type="checkbox"
            checked={Boolean(currentValue)}
            onChange={(event) => update(event.target.checked)}
          />
        </label>
      );

    case 'listOfItems': {
      const items = Array.isArray(currentValue) ? currentValue : [];
      return (
        <div className="inspector-row inspector-row--stacked">
          <span className="inspector-label">{field.label}</span>
          {items.map((item, index) => (
            <div key={index} className="inspector-list-item">
              <span className="inspector-list-index">#{index + 1}</span>
              {(field.itemFields ?? []).map((itemField) => (
                <InspectorFields
                  key={itemField.key}
                  blockId={blockId}
                  props={item as Record<string, unknown>}
                  field={itemField}
                  onChange={(patch) => {
                    const mergedItem = { ...(item as Record<string, unknown>), ...patch };
                    const nextItems = items.map((original, originalIndex) =>
                      originalIndex === index ? mergedItem : original
                    );
                    update(nextItems);
                  }}
                />
              ))}
              <button
                type="button"
                className="inspector-remove-item"
                onClick={() => update(items.filter((_, itemIndex) => itemIndex !== index))}
              >
                Remove
              </button>
            </div>
          ))}
          <button
            type="button"
            className="inspector-add-item"
            onClick={() => {
              const blank =
                (field.itemFields ?? []).length > 0
                  ? Object.fromEntries(
                      (field.itemFields ?? []).map((itemField) => [itemField.key, ''])
                    )
                  : {};
              update([...items, blank]);
            }}
          >
            Add item
          </button>
        </div>
      );
    }

    default:
      return null;
  }
}
